import React, { useEffect, useState } from 'react';
import './AnalyseOffreFinanciere.css';

interface Article {
  id: string;
  code: string;
  nom: string;
  prix: number;
}

interface Section {
  id: string;
  name: string;
  articles: Article[];
}

interface Prestataire {
  id: string;
  id_status: number;
  organisme: string;
  nom_entite: string;
  sections: Section[];
  totalHT: number;
  updated_at: string;
  note_financiere: number;
}

interface AnalyseOffreFinanciereProps {
  subsequentId: string;
}

const AnalyseOffreFinanciere: React.FC<AnalyseOffreFinanciereProps> = ({ subsequentId }) => {
  const [prestataires, setPrestataires] = useState<Prestataire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${subsequentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Erreur API');
        const data = await response.json();

        if (data.status !== 'success') throw new Error('Réponse API invalide');

        const commandesData = data.data.commandes || [];
        const prestatairesFormatted = commandesData.map((commande: any, index: number) => {
          let totalHT = 0;

          const sections = (commande.sections || []).map((section: any) => ({
            id: String(section.id),
            name: section.name || '',
            articles: (section.articles || []).map((article: any) => {
              const prix = article.prix || 0;
              totalHT += prix;
              return {
                id: String(article.id),
                code: String(article.code),
                description: article.description,
                nom: article.nom || '',
                prix
              };
            })
          }));
          const date = new Date(commande.updated_at || new Date());
          const dateReponse = `${date.toLocaleDateString('fr-FR')} à ${date.getHours().toString().padStart(2, '0')}h${date.getMinutes().toString().padStart(2, '0')}`;

          return {
            id: String(commande.id),
            id_status: commande.id_status,
            organisme: `Entreprise ${String.fromCharCode(65 + index)}`,
            nom_entite: commande.contrat.entite.nom,
            sections,
            totalHT,
            updated_at: dateReponse,
            note_financiere: commande.note_financiere,
          };
        });

        setPrestataires(prestatairesFormatted);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subsequentId]);

  const calculateNoteFinanciere = (prestaId: string): number => {
    const montants = prestataires.map(p => ({ id: p.id, montant: p.totalHT }));
    const currentMontant = montants.find(m => m.id === prestaId)?.montant || 0;
    const minMontant = Math.min(...montants.map(m => m.montant));
    const maxMontant = Math.max(...montants.map(m => m.montant));

    // Debug logs
    console.log('=== DEBUG calculateNoteFinanciere ===');
    console.log('prestaId:', prestaId);
    console.log('montants:', montants);
    console.log('currentMontant:', currentMontant);
    console.log('minMontant:', minMontant);
    console.log('maxMontant:', maxMontant);
    console.log('nombre de prestataires:', montants.length);

    let note = 0;

    if (montants.length <= 1) {
      note = 10;
      console.log('Cas: 1 seul prestataire -> note =', note);
    } else if (montants.length === 2) {
      note = currentMontant === minMontant ? 10 : 0;
      console.log('Cas: 2 prestataires -> note =', note);
    } else if (montants.length === 3) {
      if (currentMontant === minMontant) {
        note = 10;
      } else if (currentMontant === maxMontant) {
        note = 0;
      } else {
        note = 5;
      }
      console.log('Cas: 3 prestataires -> note =', note);
    } else {
      // Plus de 3 prestataires
      if (currentMontant === minMontant) {
        note = 10;
      } else if (currentMontant === maxMontant) {
        note = 0;
      } else {
        const plageTotal = maxMontant - minMontant;
        const position = maxMontant - currentMontant;
        note = Math.round((position / plageTotal) * 10);
        console.log('Cas: >3 prestataires -> plageTotal:', plageTotal, 'position:', position, 'note calculée:', note);
      }
    }

    console.log('Note finale pour prestataire', prestaId, ':', note);
    console.log('===============================');

    return note;
  };

  const InfoIcon = () => (
    <svg className="info-icon" viewBox="0 0 16 16" fill="#6B7280">
      <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 12C7.4 12 7 11.6 7 11C7 10.4 7.4 10 8 10C8.6 10 9 10.4 9 11C9 11.6 8.6 12 8 12ZM9 9H7V5H9V9Z" />
    </svg>
  );

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error">Erreur: {error}</div>;
  if (prestataires.length === 0) {
    return <div className="no-data">Aucune offre financière disponible pour cette opération.</div>;
  }

  return (
    <div className="of-comparatif-container">
      <div className="presta-comparaison-grid">
        {prestataires.map((presta) => {
          const isExpired = presta.id_status === 12;
          return (
            <div
              className={`presta-col ${isExpired ? "disabled" : ""}`}
              key={presta.id}
            >
              {/* Ajout du badge pour les commandes expirées */}
              {isExpired && <div className="expired-badge">Expiré</div>}

              <div className="presta-header">
                <h3>{presta.nom_entite}</h3>
                {isExpired ? (
                  <span>Offre non reçue</span>
                ) : (
                  <span>Offre reçue le {presta.updated_at}</span>
                )
                }
              </div>

              <table className="articles-table">
                <thead>
                  <tr>
                    <th>N° Article</th>
                    <th>Nom</th>
                    <th>Prix HT</th>
                  </tr>
                </thead>
                <tbody className='sections-container'>
                  {presta.sections.map(section => (
                    <React.Fragment key={section.id}>
                      <div className='section-item'>
                        <tr>
                          <td colSpan={3} className="table-section-header">{section.name}</td>
                        </tr>
                        {section.articles.map(article => (
                          <tr key={article.id} className='section-body'>
                            <td colSpan={1}>
                              <div className="article-numero">
                                <span>{article.code}</span>
                                <div className="info-icon-container">
                                  <InfoIcon />
                                  <div className="tooltip">
                                    {article.nom}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td colSpan={3}>
                              <span
                                className="article-nom"
                                title={article.nom} // Tooltip natif pour l'accessibilité
                              >
                                {article.nom && article.nom.length > 40
                                  ? `${article.nom.substring(0, 40)}...`
                                  : article.nom || ''
                                }
                              </span>
                            </td>
                            {isExpired ? (
                              <td colSpan={2}>-</td>
                            ) : (
                              <td colSpan={2}>{article.prix.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                            )}
                          </tr>
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="total-row">
                Total DPGF HT:&nbsp;
                {isExpired ? (
                  "-"
                ) : (
                  <>
                    {presta.totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </>
                )}
              </div>

              <div className="note-financiere">
                <h4>Note financière</h4>
                <div className="note-value">

                  {isExpired ? "-" : `${presta.note_financiere} / 10`}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
};

export default AnalyseOffreFinanciere;